import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, TextField, Avatar, IconButton, Button, FormControl, InputLabel, Select, MenuItem, Chip, Box, Popover, Typography } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { ExpandMore, ExpandLess, Settings as SettingsIcon } from "@mui/icons-material";
import InputMask from "react-input-mask"; // Para aplicar máscaras
import debounce from "lodash.debounce";
import { fetchData } from "../../api/client";
import { useClients } from "../../hooks/useClients";
import { useClientsGroups } from "../../hooks/useClientsGroups";
import { getClientProfilePictureUrl, getInitials } from "../../utils/utils";

const NIVEL_CONSCIENCIA_EXPLICACAO =
  "Os níveis de consciência são etapas que representam em que estágio do relacionamento ou da jornada de compra o cliente se encontra (ex.: consciente do problema, considerando soluções, decisão). Eles ajudam a organizar o pipeline e o atendimento.";

const CustomerDetails = ({ selectedConversation, onSelectedConversationUpdate }) => {
  const navigate = useNavigate();
  const [nivelConscienciaAnchor, setNivelConscienciaAnchor] = useState(null);

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const tagsToString = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");

  const [customerDetails, setCustomerDetails] = useState({
    nome: "",
    email: "",
    celular: "",
    id_grupo: null,
    tags: [],
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    potencial: 1,
    imagem: "",
  });
  const [tagInput, setTagInput] = useState("");

  const [errors, setErrors] = useState({});
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const { updateMutation } = useClients();
  const { data: groupsData } = useClientsGroups();
  const groups = Array.isArray(groupsData) ? groupsData : [];

  useEffect(() => {
    if (selectedConversation) {
      const originalCelular = selectedConversation?.celular || "";
      const celularDigits = originalCelular.replace(/\D/g, "");

      let normalizedCelular = originalCelular;

      // Se vier no formato antigo de celular (DDD + 8 dígitos),
      // ajusta apenas para exibição no campo, sem forçar update no backend aqui.
      if (celularDigits.length === 10) {
        const withNine =
          celularDigits.slice(0, 2) + "9" + celularDigits.slice(2);

        normalizedCelular = withNine.replace(
          /(\d{2})(\d{5})(\d{4})/,
          "($1) $2-$3"
        );
      }

      const clientTagsRaw =
        selectedConversation?.clientTags ?? selectedConversation?.client?.tags ?? "";
      setCustomerDetails({
        nome: selectedConversation?.nome || "",
        email: selectedConversation?.email || "",
        celular: normalizedCelular,
        id_grupo: selectedConversation?.id_grupo ?? null,
        tags: parseTags(clientTagsRaw),
        cep: selectedConversation?.cep || "",
        endereco: selectedConversation?.endereco || "",
        numero: selectedConversation?.numero || "",
        complemento: selectedConversation?.complemento || "",
        bairro: selectedConversation?.bairro || "",
        cidade: selectedConversation?.cidade || "",
        uf: selectedConversation?.uf || "",
        potencial: selectedConversation?.clientPotential || 1,
        imagem:
          selectedConversation?.client?.profile_picture ??
          selectedConversation?.client?.imagem ??
          selectedConversation?.clientImage ??
          selectedConversation?.imagem ??
          "",
      });
    }
  }, [selectedConversation]);

  // Função para atualizar no backend (debounced)
  const updateFn = useCallback(
    (updatedDetails) => {
      if (selectedConversation?.clientId) {
        updateMutation.mutate({ id: selectedConversation.clientId, ...updatedDetails });
      }
    },
    [selectedConversation?.clientId, updateMutation]
  );
  const debouncedUpdate = useMemo(() => debounce(updateFn, 500), [updateFn]);

  // Função para validar os campos
  const validateField = (field, value) => {
    if (field === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "E-mail inválido";
    }
    if (field === "celular" && value && !/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(value)) {
      return "Celular deve estar no formato (XX) XXXXX-XXXX";
    }
    if (field === "cep" && value && !/^\d{5}-\d{3}$/.test(value)) {
      return "CEP deve estar no formato XXXXX-XXX";
    }
    if (field === "nome" && (!value || value.trim().length === 0)) {
      return "Nome é obrigatório";
    }
    if (field === "uf" && value && value.length !== 2) {
      return "UF deve conter 2 caracteres";
    }
    return "";
  };

  // Atualiza os detalhes no estado
  const handleFieldChange = (field, value) => {
    const error = validateField(field, value);
    setErrors((prevErrors) => ({ ...prevErrors, [field]: error }));
    setCustomerDetails((prev) => ({ ...prev, [field]: value }));

    // Envia atualização apenas se não houver erro
    if (!error && field !== "cep") {
      const payload = field === "tags" ? { tags: tagsToString(value) } : { [field]: value };
      debouncedUpdate(payload);
    }

    // Se o campo for "cep", tenta buscar o endereço automaticamente
    if (field === "cep" && value.length === 9 && !error) {
      fetchAddress(value);
    }
  };

  const handleAddTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    const next = [...customerDetails.tags];
    if (next.includes(t)) {
      setTagInput("");
      return;
    }
    next.push(t);
    setCustomerDetails((prev) => ({ ...prev, tags: next }));
    setTagInput("");
    debouncedUpdate({ tags: tagsToString(next) });
    onSelectedConversationUpdate?.({ clientTags: tagsToString(next) });
  };

  const handleRemoveTag = (index) => {
    const next = customerDetails.tags.filter((_, i) => i !== index);
    setCustomerDetails((prev) => ({ ...prev, tags: next }));
    debouncedUpdate({ tags: tagsToString(next) });
    onSelectedConversationUpdate?.({ clientTags: tagsToString(next) });
  };

  // Função para buscar o endereço com base no CEP
  const fetchAddress = async (cep) => {
    setIsFetchingAddress(true);
    try {
      const cleanCep = cep.replace("-", "");
      const res = await fetchData(`/cep/lookup?cep=${cleanCep}`, "GET");
      const data = res?.data || res;

      // Atualiza os campos de endereço
      const updatedDetails = {
        endereco: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.city || data.localidade || "",
        uf: data.uf || "",
      };

      setCustomerDetails((prev) => ({ ...prev, ...updatedDetails }));
      debouncedUpdate(updatedDetails);
      setErrors((prevErrors) => ({ ...prevErrors, cep: "" })); // Remove erro de CEP, se existir
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, cep: "Erro ao buscar o endereço" }));
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const toggleAdditionalFields = () => {
    setShowAdditionalFields((prev) => !prev);
  };

  return (
    <Grid container spacing={2}>
      {/* Campos principais */}
      <Grid item xs={12}>
        <TextField
          label="Nome"
          fullWidth
          value={customerDetails.nome}
          onChange={(e) => handleFieldChange("nome", e.target.value)}
          error={!!errors.nome}
          helperText={errors.nome}
          InputProps={{
            startAdornment: (() => {
              const avatarSrc = getClientProfilePictureUrl(customerDetails.imagem);
              return (
                <Avatar src={avatarSrc || undefined} sx={{ mr: 1 }}>
                  {!avatarSrc && getInitials(customerDetails.nome)}
                </Avatar>
              );
            })(),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="E-mail"
          fullWidth
          value={customerDetails.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: <EmailIcon sx={{ mr: 1 }} />,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <InputMask
          mask="(99) 99999-9999"
          value={customerDetails.celular}
          onChange={(e) => handleFieldChange("celular", e.target.value)}
        >
          {(inputProps) => (
            <TextField
              {...inputProps}
              label="Celular"
              fullWidth
              error={!!errors.celular}
              helperText={errors.celular}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ mr: 1 }} />,
              }}
            />
          )}
        </InputMask>
      </Grid>
      <Grid item xs={12}>
        {groups.length === 0 ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Nível de consciência
            </Typography>
            <Box
              onClick={(e) => setNivelConscienciaAnchor(e.currentTarget)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Nenhum nível cadastrado — clique para saber mais
              </Typography>
            </Box>
            <Popover
              open={Boolean(nivelConscienciaAnchor)}
              anchorEl={nivelConscienciaAnchor}
              onClose={() => setNivelConscienciaAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{ sx: { p: 2, maxWidth: 360 } }}
            >
              <Typography variant="subtitle2" gutterBottom>
                O que são níveis de consciência?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {NIVEL_CONSCIENCIA_EXPLICACAO}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  setNivelConscienciaAnchor(null);
                  navigate("/awareness-stages");
                }}
              >
                Cadastrar níveis de consciência
              </Button>
            </Popover>
          </>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel id="nivel-consciencia-label">Nível de consciência</InputLabel>
            <Select
              labelId="nivel-consciencia-label"
              label="Nível de consciência"
              value={customerDetails.id_grupo ?? ""}
              onChange={(e) => handleFieldChange("id_grupo", e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.nome || `Grupo ${g.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>

      {/* Botão para expandir/colapsar */}
      <Grid
        item
        xs={12}
        textAlign="center"
        sx={{
          margin: 0, // Remove margem no Grid
          padding: 0, // Remove padding no Grid
          paddingTop: 0,
        }}
      >
        <IconButton 
          onClick={toggleAdditionalFields} 
          size="small" // Reduz o tamanho do botão
          sx={{
            m: 0, // Remove margem do IconButton
            p: 0.5, // Reduz padding do IconButton
          }}
        >
          {showAdditionalFields ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Grid>


      {/* Campos adicionais */}
      {showAdditionalFields && <>
        <Grid item xs={12}>
          <InputMask
            mask="99999-999"
            value={customerDetails.cep}
            onChange={(e) => handleFieldChange("cep", e.target.value)}
          >
            {(inputProps) => (
              <TextField
                {...inputProps}
                label="CEP"
                fullWidth
                error={!!errors.cep}
                helperText={errors.cep || (isFetchingAddress ? "Carregando endereço..." : "")}
              />
            )}
          </InputMask>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Endereço"
            fullWidth
            value={customerDetails.endereco}
            onChange={(e) => handleFieldChange("endereco", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Número"
            fullWidth
            value={customerDetails.numero}
            onChange={(e) => handleFieldChange("numero", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Bairro"
            fullWidth
            value={customerDetails.bairro}
            onChange={(e) => handleFieldChange("bairro", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Cidade"
            fullWidth
            value={customerDetails.cidade}
            onChange={(e) => handleFieldChange("cidade", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="UF"
            fullWidth
            value={customerDetails.uf}
            onChange={(e) => handleFieldChange("uf", e.target.value)}
          />
        </Grid>
      </>}

      {/* Botão para cadastro completo */}
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          href={`/clients/${selectedConversation?.clientId}`}
        >
          Ver Cadastro Completo
        </Button>
      </Grid>
    </Grid>
  );
};

export default CustomerDetails;
